/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/*!
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@                                         @@@@
 *!   @@@   THIS FILE WAS AUTOGENERATED BY TOOL   @@@@
 *!   @@@   CHANGES MUST TARGET SCRIPTS/SWAGGER   @@@@
 *!   @@@                                         @@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!
 */

import {
  _buildQueryString,
  _emptyParams,
  _encode,
  _getBasePath,
  _getRawBasePath,
} from './urls-utils'

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/networking/deployment_domain_name
 * Operation: `get-default-deployment-domain-name`
 * Gets the default Deployment Domain Name configuration value.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Deployment Domain Name was successfully retrieved. See {@link DeploymentDomainName}
 * @see 404 There is no configured Deployment Domain Name but optimistic locking was sent. (code: `networking.cname.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDefaultDeploymentDomainNameUrl(
  params: GetDefaultDeploymentDomainNameUrlParams,
): string {
  const path = _encode`/${params.regionId}/platform/configuration/networking/deployment_domain_name`
  return `${_getBasePath()}${path}`
}

export interface GetDefaultDeploymentDomainNameUrlParams {
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/networking/deployment_domain_name
 * Operation: `set-default-deployment-domain-name`
 * Saves the default Deployment Domain Name configuration value.
 * @param {string} params.version If specified, then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request). If not specified, will unconditionally upsert.
 * @param {boolean} params.skipCascadingOperations Whether or not to skip cascading operations, such as re-provisioning the Security Deployment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link DeploymentDomainName}
 * @see 200 The Deployment Domain Name was successfully saved. See {@link DeploymentDomainName}
 * @see 400 The optimistic locking version format was wrong. (code: `networking.cname.bad_version_format`). See {@link BasicFailedReply}
 * @see 404 There is no configured Deployment Domain Name but optimistic locking was sent. (code: `networking.cname.not_found`). See {@link BasicFailedReply}
 * @see 409 There was an optimistic locking version conflict. (code: `networking.cname.version_conflict`). See {@link BasicFailedReply}
 * @see 412 skip_cascading_operations was false but the Security Deployment already had a pending plan. (code: `security_deployment.cluster_pending_plan_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setDefaultDeploymentDomainNameUrl(
  params: SetDefaultDeploymentDomainNameUrlParams,
): string {
  const queryString = _buildQueryString({
    version: params.version,
    skip_cascading_operations:
      params.skipCascadingOperations === undefined ? false : params.skipCascadingOperations,
  })
  const path = _encode`/${params.regionId}/platform/configuration/networking/deployment_domain_name`
  return `${_getBasePath()}${path}${queryString}`
}

export interface SetDefaultDeploymentDomainNameUrlParams {
  version?: string | null
  skipCascadingOperations?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/networking/deployment_domain_name/{resource_kind}
 * Operation: `get-resource-kind-deployment-domain-name`
 * Gets Deployment Domain Name configuration value for a resource kind.
 * @param {string} params.resourceKind The kind of resource you want to manage a Deployment Domain Name for.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Deployment Domain Name was successfully retrieved. See {@link DeploymentDomainName}
 * @see 404 There is no configured Deployment Domain Name but optimistic locking was sent. (code: `networking.cname.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getResourceKindDeploymentDomainNameUrl(
  params: GetResourceKindDeploymentDomainNameUrlParams,
): string {
  const path = _encode`/${params.regionId}/platform/configuration/networking/deployment_domain_name/${params.resourceKind}`
  return `${_getBasePath()}${path}`
}

export interface GetResourceKindDeploymentDomainNameUrlParams {
  resourceKind: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/networking/deployment_domain_name/{resource_kind}
 * Operation: `set-resource-kind-deployment-domain-name`
 * Saves the Deployment Domain Name configuration value for a resource kind.
 * @param {string} params.resourceKind The kind of resource you want to manage a Deployment Domain Name for.
 * @param {string} params.version If specified, then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request). If not specified, will unconditionally upsert.
 * @param {boolean} params.skipCascadingOperations Whether or not to skip cascading operations, such as re-provisioning the Security Deployment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link DeploymentDomainName}
 * @see 200 The Deployment Domain Name was successfully saved. See {@link DeploymentDomainName}
 * @see 400 The optimistic locking version format was wrong. (code: `networking.cname.bad_version_format`). See {@link BasicFailedReply}
 * @see 404 There is no configured Deployment Domain Name but optimistic locking was sent. (code: `networking.cname.not_found`). See {@link BasicFailedReply}
 * @see 409 There was an optimistic locking version conflict. (code: `networking.cname.version_conflict`). See {@link BasicFailedReply}
 * @see 412 skip_cascading_operations was false but the Security Deployment already had a pending plan. (code: `security_deployment.cluster_pending_plan_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setResourceKindDeploymentDomainNameUrl(
  params: SetResourceKindDeploymentDomainNameUrlParams,
): string {
  const queryString = _buildQueryString({
    version: params.version,
    skip_cascading_operations:
      params.skipCascadingOperations === undefined ? false : params.skipCascadingOperations,
  })
  const path = _encode`/${params.regionId}/platform/configuration/networking/deployment_domain_name/${params.resourceKind}`
  return `${_getBasePath()}${path}${queryString}`
}

export interface SetResourceKindDeploymentDomainNameUrlParams {
  resourceKind: string
  version?: string | null
  skipCascadingOperations?: boolean | null
  regionId: string
}
